
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import Tag from '../../../components/base/Tag';

interface ForumPost {
  id: number;
  title: string;
  content: string;
  author_name: string;
  category: string;
  created_at: string;
  updated_at: string;
  replies_count: number;
  is_pinned: boolean;
}

interface ForumReply {
  id: number;
  post_id: number;
  content: string;
  author_name: string;
  created_at: string;
}

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<ForumPost | null>(null);
  const [replies, setReplies] = useState<ForumReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [newReply, setNewReply] = useState({
    content: '',
    author_name: '匿名'
  });

  useEffect(() => {
    if (id) {
      fetchPostDetail();
    }
  }, [id]);

  const fetchPostDetail = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/forum-api/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        throw new Error(`Failed to fetch post: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setPost(data.post);
      setReplies(data.replies);
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(null);
      setReplies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newReply.content.trim()) {
      alert('返信内容を入力してください');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/forum-api/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newReply,
          post_id: parseInt(id!)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || '返信に失敗しました');
        return;
      }

      setNewReply({ content: '', author_name: '匿名' });
      setShowReplyForm(false);
      fetchPostDetail();
    } catch (error) {
      console.error('Error creating reply:', error);
      alert('返信に失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">読み込み中...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              投稿が見つかりません
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              指定された投稿は存在しないか、削除されています。
            </p>
            <Link to="/forum">
              <Button>掲示板に戻る</Button>
            </Link>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* パンくずナビ */}
        <div className="mb-6">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/forum" className="hover:text-blue-600 dark:hover:text-blue-400">
              掲示板
            </Link>
            <span>›</span>
            <span className="text-gray-900 dark:text-white">{post.title}</span>
          </nav>
        </div>

        {/* 元投稿 */}
        <Card className="p-6 mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <Tag variant="category">{post.category}</Tag>
            {post.is_pinned && (
              <div className="flex items-center justify-center w-5 h-5">
                <i className="ri-pushpin-fill text-yellow-500"></i>
              </div>
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ID: {post.id}
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {post.title}
          </h1>
          
          <div className="prose dark:prose-invert max-w-none mb-6">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {post.content}
            </p>
          </div>
          
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <span>投稿者: {post.author_name}</span>
              <div className="flex items-center space-x-1">
                <div className="flex items-center justify-center w-4 h-4">
                  <i className="ri-message-2-line"></i>
                </div>
                <span>{replies.length} 返信</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span>作成: {new Date(post.created_at).toLocaleString('ja-JP')}</span>
              {post.updated_at !== post.created_at && (
                <span>更新: {new Date(post.updated_at).toLocaleString('ja-JP')}</span>
              )}
            </div>
          </div>
        </Card>

        {/* 返信フォーム */}
        <div className="mb-6">
          <Button 
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="whitespace-nowrap"
          >
            <i className="ri-reply-line mr-2"></i>
            返信する
          </Button>
        </div>

        {showReplyForm && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              返信を投稿
            </h2>
            <form onSubmit={handleSubmitReply} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  投稿者名
                </label>
                <input
                  type="text"
                  value={newReply.author_name}
                  onChange={e => setNewReply(prev => ({ ...prev, author_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="匿名"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  返信内容 *
                </label>
                <textarea
                  value={newReply.content}
                  onChange={e => setNewReply(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  rows={4}
                  placeholder="返信内容を入力してください..."
                  maxLength={500}
                  required
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {newReply.content.length}/500文字
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button type="submit" className="whitespace-nowrap">
                  返信する
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowReplyForm(false)}
                  className="whitespace-nowrap"
                >
                  キャンセル
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* 返信一覧 */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            返信 ({replies.length})
          </h2>
          
          {replies.length === 0 ? (
            <Card className="p-8 text-center">
              <i className="ri-message-line text-4xl text-gray-400 dark:text-gray-600 mb-4"></i>
              <p className="text-gray-600 dark:text-gray-400">
                まだ返信がありません。最初の返信をしてみませんか？
              </p>
            </Card>
          ) : (
            replies.map((reply, index) => (
              <Card key={reply.id} className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {reply.author_name}
                        </span>
                        <span className="text-sm text-gray-5dark:text-gray-400">
                          ID: {reply.id}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(reply.created_at).toLocaleString('ja-JP')}
                      </span>
                    </div>
                    <div className="prose dark:prose-invert max-w-none">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {reply.content}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* 戻るボタン */}
        <div className="mt-8 text-center">
          <Link to="/forum">
            <Button variant="outline" className="whitespace-nowrap">
              <i className="ri-arrow-left-line mr-2"></i>
              掲示板に戻る
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
